// src/games/core/session/useGameSession.ts
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  FinishErrorView,
  finishErrorInputFrom,
  translateFinishError,
} from '../finish/translateFinishError';
import { useStopwatch } from './useStopwatch';

export interface FinishResult {
  points: number;
  maxPoints: number;
  correct?: number;
  total?: number;
}

export type SessionStatus = 'loading' | 'playing' | 'finishing' | 'failed' | 'finished';

/** Envia o fim ao servidor; pode devolver pontos/total corrigidos pelo servidor. */
export type FinishSender = (seconds: number) => Promise<Partial<FinishResult> | void>;

export interface UseGameSessionOptions {
  /**
   * Jogo, progresso E histórico carregados: só então o relógio anda e o "salvar ao sair" vale.
   * Só pode virar `true` depois que ambos terminaram, pois `alreadyFinished` é lido uma única
   * vez, no primeiro `ready`; um valor que chegue depois é ignorado.
   */
  ready: boolean;
  /** Tempo já salvo em sessões anteriores (segundos). */
  savedSeconds: number;
  /** Jogo reaberto já finalizado: mostra o resultado e fica só leitura. */
  alreadyFinished: FinishResult | null;
  /**
   * Salva o progresso parcial; erros são engolidos (sair nunca quebra a tela).
   * Só é chamado enquanto o jogo está em andamento e antes de qualquer tentativa de fim.
   */
  onSaveProgress: (seconds: number) => unknown;
}

export interface GameSession {
  status: SessionStatus;
  seconds: number;
  result: FinishResult | null;
  error: FinishErrorView | null;
  modalVisible: boolean;
  isPlaying: boolean;
  finish: (local: FinishResult, send: FinishSender) => Promise<void>;
  retry: () => Promise<void>;
  showResult: () => void;
  hideResult: () => void;
  saveNow: () => void;
}

/**
 * Ciclo de uma partida: relógio, finalização "uma vez só", reenvio e salvar ao sair
 * (só antes da primeira tentativa de fim; um fim que falhou não volta a salvar parcial).
 * Atenção: `options.ready` deve ficar `true` apenas após progresso e histórico carregarem
 * (`alreadyFinished` é lido uma vez, no primeiro `ready`).
 */
export function useGameSession({
  ready,
  savedSeconds,
  alreadyFinished,
  onSaveProgress,
}: UseGameSessionOptions): GameSession {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [result, setResult] = useState<FinishResult | null>(null);
  const [error, setError] = useState<FinishErrorView | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const { elapsed } = useStopwatch(status === 'playing');
  const seconds = savedSeconds + elapsed;

  // Refs: os handlers de saída e o "uma vez só" leem sempre o valor mais novo.
  const statusRef = useRef(status);
  statusRef.current = status;
  const secondsRef = useRef(seconds);
  secondsRef.current = seconds;
  const saveRef = useRef(onSaveProgress);
  saveRef.current = onSaveProgress;
  const lastSendRef = useRef<FinishSender | null>(null);
  const initedRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!ready || initedRef.current) return;
    initedRef.current = true;
    if (alreadyFinished) {
      setResult(alreadyFinished);
      setStatus('finished');
      setModalVisible(true);
    } else {
      setStatus('playing');
    }
  }, [ready, alreadyFinished]);

  const saveNow = useCallback(() => {
    // Depois de uma tentativa de fim (mesmo que falhe) nada mais é salvo: se a resposta se
    // perdeu, o servidor pode já ter aplicado o fim, e um parcial o reescreveria.
    if (statusRef.current !== 'playing') return;
    try {
      const pending = saveRef.current(secondsRef.current);
      if (pending && typeof (pending as Promise<unknown>).catch === 'function') {
        (pending as Promise<unknown>).catch(() => undefined);
      }
    } catch {
      // salvar ao sair nunca quebra a tela
    }
  }, []);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') saveNow();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', saveNow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', saveNow);
      saveNow(); // saiu da tela pela navegação interna
    };
  }, [saveNow]);

  const run = useCallback(async (send: FinishSender) => {
    lastSendRef.current = send;
    statusRef.current = 'finishing';
    setStatus('finishing');
    setError(null);
    setModalVisible(true);
    try {
      const server = await send(secondsRef.current);
      if (!mountedRef.current) return;
      if (server) {
        // `undefined` do servidor não pode apagar o valor local
        const defined = Object.fromEntries(
          Object.entries(server).filter(([, value]) => value !== undefined)
        ) as Partial<FinishResult>;
        setResult((prev) => (prev ? { ...prev, ...defined } : prev));
      }
      setStatus('finished');
    } catch (err) {
      if (!mountedRef.current) return;
      const translated = translateFinishError(finishErrorInputFrom(err));
      setError(translated);
      setStatus(translated.kind === 'unknown' ? 'failed' : 'finished');
    }
  }, []);

  const finish = useCallback(
    async (local: FinishResult, send: FinishSender) => {
      if (statusRef.current !== 'playing') return; // uma vez só
      setResult(local);
      await run(send);
    },
    [run]
  );

  const retry = useCallback(async () => {
    if (statusRef.current !== 'failed' || !lastSendRef.current) return;
    await run(lastSendRef.current);
  }, [run]);

  const showResult = useCallback(() => setModalVisible(true), []);
  const hideResult = useCallback(() => setModalVisible(false), []);

  return {
    status,
    seconds,
    result,
    error,
    modalVisible,
    isPlaying: status === 'playing',
    finish,
    retry,
    showResult,
    hideResult,
    saveNow,
  };
}
